// @ts-check
/**
 * TypingEngine — čistá logika cvičenia. Žiadny DOM, žiadny framework.
 *
 * Vstupné udalosti dodáva UI vrstva (tá rieši `event.key`, mŕtve klávesy, IME,
 * paste a stratu fokusu). Engine iba rozhoduje o správnosti, počíta metriky
 * a drží stav znakov. Vďaka tomu je celý engine testovateľný v Node bez prehliadača.
 */
import { computeMetrics, normalizeBase, DEFAULT_NET_PENALTY } from './metrics.js';
import { strokeCost } from './strojopis.js';

/** @typedef {'pending'|'correct'|'wrong'} CharStatus */
/** @typedef {'correct'|'flow'} ErrorModeName */

export const ErrorMode = {
  /** Pedagogický režim: chybný znak sa musí opraviť, kurzor nepostupuje. */
  CORRECT: /** @type {ErrorModeName} */ ('correct'),
  /** Testovací režim: chyba sa počíta a ide sa ďalej. */
  FLOW: /** @type {ErrorModeName} */ ('flow'),
};

/** Ako dlho (ms) je ešte považovaná za "živú" pauza po poslednom úhoze. */
export const ACTIVE_WINDOW_MS = 5000;

/** @typedef {Object} EngineOptions
 * @property {string} text
 * @property {ErrorModeName} [errorMode]
 * @property {boolean} [allowBackspace]
 * @property {boolean} [tolerateDiacritics]
 * @property {number} [netPenalty]
 * @property {boolean} [officialStrokes]  počítať oficiálne údery (kap. 13 manuálu)
 * @property {(code: string, char: string) => void} [onKeyObserved]
 */

export class TypingEngine {
  /** @param {EngineOptions} options */
  constructor(options) {
    this.text = String(options.text ?? '');
    this.errorMode = options.errorMode ?? ErrorMode.CORRECT;
    this.allowBackspace = options.allowBackspace !== false;
    this.tolerateDiacritics = !!options.tolerateDiacritics;
    this.netPenalty = options.netPenalty ?? DEFAULT_NET_PENALTY;
    /** Počítať údery podľa oficiálnej metodiky (nie ako stlačenia klávesov). */
    this.officialStrokes = options.officialStrokes !== false;
    /** @type {((code: string, char: string) => void)|null} */
    this.onKeyObserved = options.onKeyObserved ?? null;
    /** @type {Set<() => void>} */
    this.listeners = new Set();

    // Polia stavu sú deklarované tu (nie v `reset()`), aby ich typová kontrola
    // videla ako vlastnosti triedy a nie ako `any`.
    /** @type {CharStatus[]} */
    this.status = [];
    /** @type {number} */
    this.index = 0;
    /** @type {number|null} */
    this.startedAt = null;
    /** @type {number|null} */
    this.lastStrokeAt = null;
    /** @type {number|null} */
    this.endedAt = null;
    /** @type {number} */
    this.grossStrokes = 0;
    /** @type {number} */
    this.keystrokes = 0;
    /** @type {string} */
    this.lastChar = '';
    /** @type {number} */
    this.correct = 0;
    /** @type {number} */
    this.errors = 0;
    /** @type {number} */
    this.softErrors = 0;
    /** @type {number} */
    this.skipped = 0;
    /** @type {Map<string, number>} */
    this.perKeyErrors = new Map();
    /** @type {number[]} */
    this.intervals = [];

    this.reset();
  }

  reset() {
    /** @type {CharStatus[]} */
    this.status = new Array(this.text.length).fill('pending');
    this.index = 0;
    /** @type {number|null} */
    this.startedAt = null;
    /** @type {number|null} */
    this.lastStrokeAt = null;
    /** @type {number|null} */
    this.endedAt = null;
    /** Oficiálne údery (kap. 13 manuálu), nie stlačenia klávesov. */
    this.grossStrokes = 0;
    /** Fyzické stlačenia klávesov — slúžia len na kontrolu tempa. */
    this.keystrokes = 0;
    /** Posledný prijatý znak (kvôli pravidlu pre skupinu číslic). */
    this.lastChar = '';
    this.correct = 0;
    this.errors = 0;
    this.softErrors = 0;
    this.skipped = 0;
    /** @type {Map<string, number>} */
    this.perKeyErrors = new Map();
    /** @type {number[]} */
    this.intervals = [];
    this.emit();
  }

  /** @param {() => void} listener */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit() {
    for (const listener of this.listeners) listener();
  }

  get length() {
    return this.text.length;
  }

  get finished() {
    return this.length > 0 && this.index >= this.length;
  }

  get progress() {
    return this.length === 0 ? 0 : this.index / this.length;
  }

  /** Očakávaný znak na aktuálnej pozícii (alebo null na konci). */
  get expectedChar() {
    return this.index < this.length ? this.text[this.index] : null;
  }

  /**
   * Je engine "aktívny"? Teda beží čas a počíta sa do trvania.
   * @param {number} now
   */
  isActive(now) {
    if (this.startedAt === null || this.finished) return false;
    if (this.lastStrokeAt === null) return false;
    return now - this.lastStrokeAt < ACTIVE_WINDOW_MS;
  }

  /**
   * Zapíše jeden znak.
   *
   * POZOR: `grossStrokes` nie sú stlačenia klávesov, ale OFICIÁLNE ÚDERY
   * (kap. 13 manuálu predlohy) — číslice a veľké písmená sa počítajú inak než 1.
   * Fyzické stlačenia sa počítajú zvlášť do `keystrokes`.
   *
   * @param {string} char              znak z `event.key` (Enter → '\n')
   * @param {number} now               Date.now()
   * @param {string} [code]            fyzická pozícia z `event.code`
   * @param {{shift?: boolean, capsLock?: boolean}} [context]
   * @returns {'correct'|'wrong'|'ignored'|'finished'}
   */
  typeChar(char, now, code, context = {}) {
    if (this.finished) return 'finished';
    if (char.length === 0) return 'ignored';

    const expected = this.text[this.index];
    const normalized = char === 'Enter' ? '\n' : char;
    const exact = normalized === expected;
    let isSoft = false;
    let isCorrect = exact;

    if (!exact && this.tolerateDiacritics && expected !== '\n') {
      // Zhovievavý režim: "s" namiesto "š" prejde, ale započíta sa ako mäkká chyba.
      const sameBase = normalizeBase(normalized) === normalizeBase(expected);
      if (sameBase && normalized.length === 1 && expected.length === 1) {
        isCorrect = true;
        isSoft = true;
      }
    }

    if (this.onKeyObserved && code) this.onKeyObserved(code, normalized);

    if (this.startedAt === null) this.startedAt = now;
    if (this.lastStrokeAt !== null) this.intervals.push(now - this.lastStrokeAt);
    this.lastStrokeAt = now;

    // Oficiálne údery: v skupine číslic sa Shift počíta len raz za skupinu.
    const digitRun = this.lastChar >= '0' && this.lastChar <= '9' && normalized >= '0' && normalized <= '9';
    this.grossStrokes += this.officialStrokes
      ? strokeCost(normalized, { shift: context.shift, capsLock: context.capsLock, digitRun })
      : 1;
    this.keystrokes += 1;
    this.lastChar = normalized;

    if (isCorrect) {
      this.correct += 1;
      if (isSoft) this.softErrors += 1;
      // V opravnom režime ostáva znak, ktorý bol raz chybný, označený ako chybný —
      // pedagogicky je správne, aby žiak videl, kde zaváhal.
      if (this.status[this.index] !== 'wrong') this.status[this.index] = 'correct';
      this.index += 1;
    } else {
      this.errors += 1;
      this.perKeyErrors.set(expected, (this.perKeyErrors.get(expected) ?? 0) + 1);
      this.status[this.index] = 'wrong';
      if (this.errorMode === ErrorMode.FLOW) this.index += 1;
    }

    if (this.index >= this.length) this.endedAt = now;
    this.emit();
    return isCorrect ? 'correct' : 'wrong';
  }

  /**
   * Backspace. Ak je zakázaný, správa sa ako `skip()`.
   * @param {number} now
   * @returns {'moved'|'skipped'|'ignored'|'finished'}
   */
  backspace(now) {
    if (this.finished) return 'finished';
    if (!this.allowBackspace) return this.skip(now);
    if (this.index === 0) return 'ignored';
    this.index -= 1;
    this.status[this.index] = 'pending';
    this.lastStrokeAt = now;
    this.emit();
    return 'moved';
  }

  /**
   * Preskočenie znaku (Tab, prípadne Backspace v zakázanom režime).
   * Počíta sa ako chyba, ale nie ako úhoz — preto znižuje presnosť.
   * @param {number} now
   */
  skip(now) {
    if (this.finished) return 'finished';
    if (this.startedAt === null) this.startedAt = now;
    this.errors += 1;
    this.skipped += 1;
    this.perKeyErrors.set(this.text[this.index], (this.perKeyErrors.get(this.text[this.index]) ?? 0) + 1);
    this.status[this.index] = 'wrong';
    this.index += 1;
    this.lastStrokeAt = now;
    if (this.index >= this.length) this.endedAt = now;
    this.emit();
    return 'skipped';
  }

  /**
   * Trvanie: po skončení je zamrznuté, počas písania rastie (aktívny čas).
   * @param {number} now
   */
  durationMs(now) {
    if (this.startedAt === null) return 0;
    const end = this.endedAt ?? now;
    return Math.max(0, end - this.startedAt);
  }

  /**
   * Live metriky pre UI (počítajú sa z aktuálneho stavu).
   * @param {number} now
   */
  liveMetrics(now) {
    return computeMetrics({
      grossStrokes: this.grossStrokes,
      correct: this.correct,
      errors: this.errors,
      softErrors: this.softErrors,
      durationMs: this.durationMs(now),
      netPenalty: this.netPenalty,
    });
  }

  /**
   * Pripočíta údery za stlačenie klávesu, ktorý sám nič nepíše (napr. CapsLock).
   * Oficiálne pravidlo počíta zapnutie aj vypnutie zámky preraďovača ako úder,
   * a preto sa musí započítať aj vtedy, keď sa textová pozícia nemení.
   * @param {number} cost
   */
  addModifierStrokes(cost = 1) {
    if (this.finished) return;
    this.grossStrokes += cost;
    this.keystrokes += 1;
    this.emit();
  }

  /** Najhoršie klávesy podľa počtu chýb. */
  worstKeys(limit = 5) {
    return [...this.perKeyErrors.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([ch, count]) => ({ char: ch, errors: count }));
  }

  /**
   * Podozrivo rýchle tempo (ochrana proti "nalepenému" textu).
   * Počíta sa z FYZICKÝCH stlačení, nie z oficiálnych úderov.
   * @param {number} now
   */
  isPlausiblePace(now) {
    const ms = this.durationMs(now);
    if (this.keystrokes < 20 || ms <= 0) return true;
    const cpm = this.keystrokes / (ms / 60000);
    return cpm <= 1500;
  }

  /**
   * Výsledok na uloženie do histórie.
   * @param {number} now
   * @param {{id?: string, title?: string}} [lesson]
   */
  toResult(now, lesson = {}) {
    const metrics = this.liveMetrics(now);
    return {
      lessonId: lesson.id ?? null,
      lessonTitle: lesson.title ?? null,
      finishedAt: now,
      durationMs: metrics.durationMs,
      // Hrubé údery podľa oficiálnej metodiky (pozor: nie sú to stlačenia klávesov).
      grossStrokes: this.grossStrokes,
      keystrokes: this.keystrokes,
      correct: this.correct,
      errors: this.errors,
      skipped: this.skipped,
      softErrors: this.softErrors,
      wrongChars: this.status.filter((s) => s === 'wrong').length,
      grossCpm: metrics.grossCpm,
      netCpm: metrics.netCpm,
      wpm: metrics.wpm,
      accuracy: Math.round(metrics.accuracy * 1000) / 1000,
      errorRatePercent: metrics.errorRatePercent,
      perKeyErrors: [...this.perKeyErrors.entries()].map(([char, errors]) => ({ char, errors })),
      completed: this.finished,
    };
  }
}
